/**
 * PAPPO — Avatar Component
 * Círculo com iniciais coloridas derivadas do nome.
 * Tamanhos: sm (32px), md (40px), lg (56px).
 */

import styles from './Avatar.module.css';

export interface AvatarProps {
  /** Nome para gerar iniciais e cor */
  name: string;
  /** URL do avatar (se disponível) */
  src?: string | null;
  /** Tamanho */
  size?: 'sm' | 'md' | 'lg';
  /** Indicador online */
  online?: boolean;
  /** Classe CSS adicional */
  className?: string;
}

/** Gera uma cor HSL estável a partir do nome */
function nameToColor(name: string): string {
  let hash = 0;
  for (let i = 0; i < name.length; i++) {
    hash = name.charCodeAt(i) + ((hash << 5) - hash);
  }
  const hue = Math.abs(hash % 360);
  return `hsl(${hue}, 55%, 50%)`;
}

/** Extrai até 2 iniciais do nome */
function getInitials(name: string): string {
  const parts = name.trim().split(/\s+/);
  if (parts.length === 1) return parts[0].charAt(0).toUpperCase();
  return (parts[0].charAt(0) + parts[parts.length - 1].charAt(0)).toUpperCase();
}

export function Avatar({ name, src, size = 'md', online, className = '' }: AvatarProps) {
  const initials = getInitials(name);
  const bgColor = nameToColor(name);

  const classes = [styles.avatar, styles[size], className].filter(Boolean).join(' ');

  return (
    <div className={classes} role="img" aria-label={name}>
      {src ? (
        <img src={src} alt={name} className={styles.image} />
      ) : (
        <span className={styles.initials} style={{ backgroundColor: bgColor }}>
          {initials}
        </span>
      )}
      {online !== undefined && (
        <span
          className={`${styles.status} ${online ? styles.online : styles.offline}`}
          aria-label={online ? 'Online' : 'Offline'}
        />
      )}
    </div>
  );
}
